import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStorefront } from './useStorefront';

export type Locale = 'de' | 'en' | 'it' | 'fr';

export const LOCALES: Locale[] = ['de', 'en', 'it', 'fr'];
/**
 * Locales that appear as a URL prefix (e.g. /en, /it, /fr) on CH/DE/AT.
 * sv and sq (future SE/AL native defaults) will NEVER be URL segments; they
 * render at the root of their domain. Translation infra lands in a later step.
 */
export const NON_DEFAULT_LOCALES: Locale[] = ['en', 'it', 'fr'];
export const DEFAULT_LOCALE: Locale = 'de';

const STORED_LOCALE_KEY = 'bf_locale';

function readStoredLocale(): Locale | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORED_LOCALE_KEY);
    return v && (LOCALES as string[]).includes(v) ? (v as Locale) : null;
  } catch {
    return null;
  }
}

export const LOCALE_META: Record<Locale, { label: string; flagCode: string; hreflang: string }> = {
  de: { label: 'Deutsch', flagCode: 'CH', hreflang: 'de-CH' },
  en: { label: 'English', flagCode: 'US', hreflang: 'en' },
  it: { label: 'Italiano', flagCode: 'IT', hreflang: 'it-CH' },
  fr: { label: 'Français', flagCode: 'FR', hreflang: 'fr-CH' },
};

/**
 * Extended locale metadata for future storefronts (SE, AL). Kept separate from
 * LOCALE_META so it doesn't force sv/sq into every existing Record<Locale, …>
 * across the codebase. Translation infra + full type widening lands in a later
 * step; for now this is just the display/hreflang lookup.
 */
export const EXTENDED_LOCALE_META: Record<'sv' | 'sq', { label: string; flagCode: string; hreflang: string }> = {
  sv: { label: 'Svenska', flagCode: 'SE', hreflang: 'sv-SE' },
  sq: { label: 'Shqip', flagCode: 'AL', hreflang: 'sq-AL' },
};

/** Route segment translations */
export const ROUTE_SEGMENTS: Record<string, Record<Locale, string>> = {
  produkt: { de: 'produkt', en: 'product', it: 'prodotto', fr: 'produit' },
  blog: { de: 'blog', en: 'blog', it: 'blog', fr: 'blog' },
  checkout: { de: 'checkout', en: 'checkout', it: 'checkout', fr: 'checkout' },
  anmelden: { de: 'anmelden', en: 'login', it: 'accesso', fr: 'connexion' },
  registrieren: { de: 'registrieren', en: 'register', it: 'registrazione', fr: 'inscription' },
  konto: { de: 'konto', en: 'account', it: 'account', fr: 'compte' },
  impressum: { de: 'impressum', en: 'imprint', it: 'impressum', fr: 'mentions-legales' },
  agb: { de: 'agb', en: 'terms', it: 'termini', fr: 'conditions' },
  datenschutz: { de: 'datenschutz', en: 'privacy', it: 'privacy', fr: 'confidentialite' },
  sitemap: { de: 'sitemap', en: 'sitemap', it: 'sitemap', fr: 'sitemap' },
  'big-punkte': { de: 'big-punkte', en: 'big-points', it: 'punti-fedelta', fr: 'points-fidelite' },
  kontakt: { de: 'kontakt', en: 'contact', it: 'contatto', fr: 'contact' },
};

interface LanguageContextValue {
  locale: Locale;
  isDefault: boolean;
  /** Build a localized path. Pass the German (default) path. */
  localePath: (dePath: string) => string;
  /** Switch to another locale, keeping the current page */
  switchLocale: (newLocale: Locale) => void;
  /** Get the path prefix for the current locale */
  prefix: string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

/** Detect locale from the first URL segment */
export function detectLocaleFromPath(pathname: string): { locale: Locale; rest: string } {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0] as Locale;
  if (NON_DEFAULT_LOCALES.includes(first)) {
    return { locale: first, rest: '/' + segments.slice(1).join('/') };
  }
  return { locale: DEFAULT_LOCALE, rest: pathname };
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const storefront = useStorefront();

  const { locale, rest } = useMemo(
    () => {
      // Priority: URL prefix > stored user choice > storefront default.
      // CH/DE with no stored key → identical to previous behaviour.
      const fromPath = detectLocaleFromPath(location.pathname);
      const segments = location.pathname.split('/').filter(Boolean);
      const hasPrefix = NON_DEFAULT_LOCALES.includes(segments[0] as Locale);
      if (hasPrefix) return fromPath;
      const stored = readStoredLocale();
      if (stored) return { locale: stored, rest: fromPath.rest };
      // Narrow storefront default to the current content-Locale union.
      // sv/sq have no translation infra yet → fall back to 'en'. This only
      // affects SE/AL, which are not live in LIVE_STOREFRONTS.
      const sd = storefront.defaultLocale;
      const narrowed: Locale =
        sd === 'de' || sd === 'en' || sd === 'it' || sd === 'fr' ? sd : 'en';
      return { locale: narrowed, rest: fromPath.rest };
    },
    [location.pathname, storefront.defaultLocale]
  );

  const isDefault = !NON_DEFAULT_LOCALES.includes(locale);
  const prefix = isDefault ? '' : `/${locale}`;

  const localePath = (dePath: string): string => {
    if (isDefault) return dePath;
    // Translate known route segments
    let translated = dePath;
    for (const [deSegment, translations] of Object.entries(ROUTE_SEGMENTS)) {
      const localeSegment = translations[locale];
      if (localeSegment !== deSegment) {
        translated = translated.replace(`/${deSegment}`, `/${localeSegment}`);
      }
    }
    return `${prefix}${translated}`;
  };

  const switchLocale = (newLocale: Locale) => {
    if (newLocale === locale) return;
    const currentRest = rest || '/';

    // Reverse-translate current path segments back to German
    let dePath = currentRest;
    for (const [deSegment, translations] of Object.entries(ROUTE_SEGMENTS)) {
      const currentSegment = translations[locale];
      if (currentSegment !== deSegment) {
        dePath = dePath.replace(`/${currentSegment}`, `/${deSegment}`);
      }
    }

    // Now translate to new locale
    const newPrefix = newLocale === DEFAULT_LOCALE ? '' : `/${newLocale}`;
    let newPath = dePath;
    for (const [deSegment, translations] of Object.entries(ROUTE_SEGMENTS)) {
      const newSegment = translations[newLocale];
      if (newSegment !== deSegment) {
        newPath = newPath.replace(`/${deSegment}`, `/${newSegment}`);
      }
    }

    navigate(`${newPrefix}${newPath}`);
  };

  const value = useMemo(
    () => ({ locale, isDefault, localePath, switchLocale, prefix }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, location.pathname]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
